import { faClose } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Box, { BoxProps } from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Pagination from "@mui/material/Pagination";
import Select from "../../common/Select";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { FormikHelpers, useFormik } from "formik";
import moment from "moment";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { deleteLocker, sendHealthCheck, submitLocker } from "../../api/lockers";
import DeleteConfirmationDialog from "../../common/DeleteConfirmationDialog";
import { Bin, Location, Locker } from "../../entities";
import { useAppDispatch, useAppSelector } from "../../hooks";
import {
  getLocations,
  selectItems,
  selectItemsCount,
} from "../../store/locationsSlice";
import { selectItemById } from "../../store/lockersSlice";
import { getCreateLockerValidationSchema } from "../../validation";
import AddBinDialog from "../AddBinDialog";
import HealthCheckDialog, { HealthCheckType } from "../HealthCheckDialog";
import BinItem from "./BinItem";
import "./style.scss";
import {
  EnumNotificationType,
  showNotification,
} from "../../store/notificationSlice";

interface FormikValues {
  lockerName: string;
  location?: Location.Type;
  bins: Partial<Bin.Type>[];
  start: moment.Moment;
  end: moment.Moment;
}

interface OwnProps {
  id?: string;
  open: boolean;
  onClose: (shouldRefetch?: boolean) => void;
}

const ITEMS_PER_PAGE = 3;

const LockerDialog = ({ id, open, onClose }: OwnProps) => {
  const dispatch = useAppDispatch();
  const { t: getTranslationByLabel, i18n } = useTranslation();
  const organization = useAppSelector((state) => state.user.self?.organization);
  const locations = useAppSelector(selectItems);
  const itemsCount = useAppSelector(selectItemsCount);
  const locker = useAppSelector((state) => selectItemById(state, id));
  const [page, setPage] = useState(1);
  const [healthCheckType, setHealthCheckType] = useState<HealthCheckType>();
  const [binDialogOpen, setBinDialogOpen] = useState(false);
  const [binToEdit, setBinToEdit] = useState<Partial<Bin.Type>>();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  useEffect(() => {
    if (
      open &&
      (!locations.length || !itemsCount || locations.length !== itemsCount)
    ) {
      dispatch(getLocations({ page: 0, itemsPerPage: 200 }));
    }
  }, [open, locations, itemsCount]);

  useEffect(() => {
    if (locker) {
      resetForm({
        values: {
          lockerName: locker.name,
          location: locker.location,
          bins: locker.bins,
          start: moment().hour(locker.reservationStartTime).minute(0),
          end: moment().hour(locker.reservationEndTime).minute(0),
        },
      });
    }
  }, [locker]);

  useEffect(() => {
    if (!open) {
      resetForm({ values: initialFormikValues });
      setPage(1);
    }
  }, [open]);

  const initialFormikValues = useMemo<FormikValues>(
    () => ({
      lockerName: "",
      bins: [],
      start: moment().minute(0),
      end: moment().minute(0),
    }),
    []
  );

  const {
    isSubmitting,
    values,
    errors,
    touched,
    resetForm,
    setSubmitting,
    dirty,
    setFieldTouched,
    submitForm,
    handleChange,
  } = useFormik<FormikValues>({
    initialValues: initialFormikValues,
    validationSchema: getCreateLockerValidationSchema(
      getTranslationByLabel("required-field"),
      getTranslationByLabel("long-string")
    ),
    onSubmit: async (
      values: FormikValues,
      { setSubmitting }: FormikHelpers<FormikValues>
    ) => {
      try {
        setSubmitting(true);
        const item: Partial<Locker.Type> = {
          name: values.lockerName,
          ...values,
          organization,
          bins: values.bins as Bin.Type[],
          reservationStartTime: values.start.hour(),
          reservationEndTime: values.end.hour(),
        };
        if (!!id) {
          item.id = id;
        }
        await submitLocker(item, !id);
        setSubmitting(false);
        onClose(true);
      } catch (error: any) {
        setSubmitting(false);
        dispatch(
          showNotification({
            text: error.response.data,
            type: EnumNotificationType.Error,
          })
        );
        console.log("Error while save locker -> ", error);
      }
    },
  });

  const binsToDisplay = useMemo(() => {
    if (!values) {
      return [];
    }
    let startIndex = page - 1;
    if (page !== 1) {
      startIndex = ITEMS_PER_PAGE * (page - 1);
    }
    return values.bins.slice(startIndex, ITEMS_PER_PAGE * page);
  }, [values, page]);

  const handleDelete = useCallback(async () => {
    if (!id) {
      return;
    }
    setSubmitting(true);
    await deleteLocker(id);
    setSubmitting(false);
    setDeleteDialogOpen(false);
    onClose(true);
  }, [id, setSubmitting]);

  const handleClose = useCallback(() => {
    resetForm({
      values: initialFormikValues,
    });
    onClose();
  }, [initialFormikValues, resetForm, onClose]);

  const handleSelectChange = useCallback(
    ({ target }) => {
      const location = locations.find((el) => el.name === target.value);
      handleChange({
        target: {
          name: target.name,
          value: location,
        },
      });
    },
    [locations, values.location]
  );

  const binsLabel = useMemo(() => {
    const count = values.bins.length;
    let labelNumber = page * ITEMS_PER_PAGE;
    if (count < ITEMS_PER_PAGE || labelNumber > count) {
      labelNumber = count;
    }
    return `${labelNumber}/${count} ${getTranslationByLabel(
      "bins"
    ).toLowerCase()}`;
  }, [values, page, i18n.language]);

  return (
    <>
      <Dialog
        open={open}
        fullWidth
        maxWidth="lg"
        onClose={handleClose}
        className="locker-dialog"
      >
        <DialogTitle>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography variant="h4">
              {getTranslationByLabel(id ? "update-locker" : "create-locker")}
            </Typography>
            <IconButton
              aria-label="close"
              onClick={handleClose}
              color="primary"
            >
              <FontAwesomeIcon icon={faClose} />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent
          sx={{ padding: 0, overflow: "hidden", overflowY: "auto" }}
        >
          <Box display="flex" flexDirection="column">
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              p={2}
            >
              <Box display="flex" alignItems="flex-start">
                <Item width={250} label={getTranslationByLabel("locker-name")}>
                  <TextField
                    id="lockerName"
                    name="lockerName"
                    type="text"
                    placeholder={`${getTranslationByLabel(
                      "enter-locker-name"
                    )}...`}
                    value={values.lockerName}
                    onChange={(e) => {
                      setFieldTouched(e.target.name, true);
                      handleChange(e);
                    }}
                    error={touched.lockerName && Boolean(errors.lockerName)}
                    helperText={
                      (touched.lockerName && errors.lockerName) || " "
                    }
                  />
                </Item>
                <Item
                  width={200}
                  label={getTranslationByLabel("location-name")}
                >
                  <FormControl error={Boolean(errors.location)}>
                    <Select
                      name="location"
                      value={values.location?.name ?? undefined}
                      displayEmpty
                      placeholder={`${getTranslationByLabel(
                        "select-location"
                      )}...`}
                      onChange={handleSelectChange}
                    >
                      {locations.map((location) => (
                        <MenuItem
                          key={location.id}
                          value={location.name}
                          disabled={values.location?.id === location.id}
                        >
                          {`${location.name}, ${location.building}`}
                        </MenuItem>
                      ))}
                    </Select>
                    {Boolean(errors.location) && (
                      <FormHelperText>{errors.location}</FormHelperText>
                    )}
                  </FormControl>
                </Item>
                <Item width={120} label={getTranslationByLabel("start")}>
                  <TimePicker
                    value={values.start}
                    views={["hours"]}
                    onChange={(value) => {
                      if (value) {
                        handleChange({
                          target: {
                            name: "start",
                            value: value,
                          },
                        });
                        if (!values.end || values.end.isBefore(value)) {
                          handleChange({
                            target: {
                              name: "end",
                              value: value,
                            },
                          });
                        }
                      }
                    }}
                    ampm={false}
                    minutesStep={5}
                    renderInput={(params) => <TextField {...params} />}
                  />
                </Item>
                <Item width={120} label={getTranslationByLabel("end")}>
                  <TimePicker
                    value={values.end}
                    views={["hours"]}
                    minTime={values.start}
                    onChange={(value) => {
                      if (value) {
                        handleChange({
                          target: {
                            name: "end",
                            value: value,
                          },
                        });
                      }
                    }}
                    ampm={false}
                    minutesStep={5}
                    renderInput={(params) => <TextField {...params} />}
                  />
                </Item>
              </Box>
              <Box display="flex" minWidth="350">
                {!!id && (
                  <Button
                    fullWidth
                    sx={{
                      width: 185,
                      height: 60,
                      marginRight: "16px",
                    }}
                    variant="outlined"
                    onClick={() => {
                      sendHealthCheck(id);
                      setHealthCheckType(HealthCheckType.SINGLE);
                    }}
                  >
                    {getTranslationByLabel("send-health-check")}
                  </Button>
                )}
                <Button
                  fullWidth
                  sx={{
                    width: 185,
                    height: 60,
                  }}
                  variant="contained"
                  onClick={() => {
                    setBinDialogOpen(true);
                  }}
                >
                  {getTranslationByLabel("add-new-bin")}
                </Button>
              </Box>
            </Box>
            <Box display="flex" flexDirection="column">
              {!!values.bins.length ? (
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  width="100%"
                >
                  <Typography
                    variant="body1"
                    align="left"
                    color="primary"
                    className="label"
                  >
                    {binsLabel}
                  </Typography>
                  {binsToDisplay.map((el) => (
                    <BinItem
                      key={el.id}
                      item={el}
                      onEdit={() => {
                        setBinDialogOpen(true);
                        setBinToEdit(el);
                      }}
                    />
                  ))}
                  <Pagination
                    onChange={(_, page) => {
                      setPage(page);
                    }}
                    page={page ?? 1}
                    count={Math.ceil(values.bins.length / ITEMS_PER_PAGE)}
                    variant="outlined"
                    color="primary"
                    shape="rounded"
                  />
                </Box>
              ) : (
                <>
                  <Typography
                    textAlign="center"
                    variant="h5"
                    color="textSecondary"
                  >
                    {getTranslationByLabel("no-bins")}
                  </Typography>
                  {touched.bins && Boolean(errors.bins) && (
                    <Typography
                      textAlign="center"
                      variant="caption"
                      color="error"
                    >
                      {getTranslationByLabel("please-add-bins")}
                    </Typography>
                  )}
                </>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions className="actions">
          {!!id && (
            <>
              <Button
                className="button"
                variant="text"
                disabled={isSubmitting}
                onClick={() => {
                  setDeleteDialogOpen(true);
                }}
              >
                {getTranslationByLabel("delete")}
              </Button>
            </>
          )}
          <Button
            className="button"
            variant="contained"
            autoFocus
            disabled={isSubmitting || !dirty}
            onClick={submitForm}
          >
            {getTranslationByLabel("submit")}
          </Button>
        </DialogActions>
        <HealthCheckDialog
          open={!!healthCheckType}
          type={healthCheckType}
          onClose={() => {
            setHealthCheckType(undefined);
          }}
        />
        <AddBinDialog
          open={binDialogOpen}
          bin={binToEdit}
          onClose={() => {
            setBinDialogOpen(false);
            setBinToEdit(undefined);
          }}
          onSubmit={(item) => {
            const binWithSameNumber = values.bins.find(
              (el) => el.number === item.number
            );
            if (binWithSameNumber) {
              if (!binToEdit || binToEdit.number !== item.number) {
                dispatch(
                  showNotification({
                    text: getTranslationByLabel("bin-number-error"),
                    type: EnumNotificationType.Error,
                  })
                );
                return;
              }
            }
            if (binToEdit) {
              const objIndex = values.bins.findIndex(
                (obj) => obj.number === item.number
              );
              const newBins = [...values.bins];
              newBins[objIndex] = item;
              handleChange({
                target: {
                  name: "bins",
                  value: newBins,
                },
              });
            } else {
              handleChange({
                target: {
                  name: "bins",
                  value: [...values.bins, item],
                },
              });
            }
            setBinDialogOpen(false);
            setBinToEdit(undefined);
          }}
          onRemoveBin={() => {
            handleChange({
              target: {
                name: "bins",
                value: values.bins.filter(
                  (el) => el.number !== binToEdit?.number
                ),
              },
            });
            setBinDialogOpen(false);
            setBinToEdit(undefined);
          }}
        />
      </Dialog>
      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        // disabled={isSubmitting}
        onConfirm={handleDelete}
        onClose={() => {
          setDeleteDialogOpen(false);
        }}
      />
    </>
  );
};

interface ItemProps extends BoxProps {
  label: string;
}

const Item: React.FC<ItemProps> = ({ children, label, ...rest }: ItemProps) => (
  <Box display="flex" flexDirection="column" mr={2} {...rest}>
    <Typography variant="caption" gutterBottom textTransform="uppercase">
      {label}
    </Typography>
    {children}
  </Box>
);

export default LockerDialog;
